import { observer } from 'mobx-react';
import React from 'react';

import { Divider, GridLegacy as Grid, Switch } from '@mui/material';

import API from 'utils/api';

import Menu from '@mui/material/Menu';

import MenuItem from '@mui/material/MenuItem';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import RoleInfo from 'containers/Users/components/AddUsersModal/components/RoleInfo';
import Chip from 'components/Chip';

import { UserRole as UserRoleType, UserRoleNames } from 'core/constants';

import { RowRoleInfo } from './access/RoleInfo';

import { UpdatedUserPayload } from 'utils/events/types';

import { postMessage } from 'utils/events/broadcast';
import { EventType } from 'utils/events/constants';

import { SectionGrid } from 'components/BackgroundInformation/styles';

import { AxiosError } from 'axios';

import { getErrorMessage } from 'utils/api/errors';
import Logger from 'utils/logger';

import { colors } from 'theme/colors';

import { StyledRowTitle, RowValue, ProductLicencesGroup } from './styles';
import { useStore } from '../../../../utils/hooks/store';
import { TabTitle } from '../PersonalInformation/styles';

import RolesAtUnits from './access/RolesAtUnits';

const Row: React.FC<{ name: React.ReactNode; children: React.ReactNode; info?: React.ReactNode; id?: string }> = ({
  name,
  children,
  id,
  info,
}): React.ReactElement => (
  <>
    <SectionGrid item container xs={2} alignContent="flex-start" id={id} alignItems="center">
      <StyledRowTitle style={{ marginTop: '12px', marginBottom: '5px' }}>{name}</StyledRowTitle>
      {info}
    </SectionGrid>
    <SectionGrid item container xs={10}>
      {children}
    </SectionGrid>
  </>
);

import useMappedFields from '../../hooks/useMappedFields';

const ProductAccess: React.FunctionComponent = () => {
  const {
    userDetails: { details },
    notification,
    session,
  } = useStore();

  const [updating, setUpdating] = React.useState(false);
  const [roleMenuAnchorEl, setRoleMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const { scimEnabled } = useMappedFields();

  if (!details) {
    return null;
  }

  // Role and licences come from the identity provider's group membership in a SCIM organisation.
  const disabled = updating || !details.isActive || scimEnabled;

  const onChangeLicense = async (licenseName: string, licenseValue: boolean): Promise<void> => {
    setUpdating(true);
    try {
      const response = await API.user.updateUserData(details.uuid, {
        licenses: { ...details.licenses, [licenseName]: licenseValue },
      })();
      const payload: UpdatedUserPayload = response.data;
      postMessage(EventType.UpdatedUser, payload);
      notification.enqueueSuccessSnackbar('Licence updated successfully!');
    } catch (e) {
      Logger.error('Invalid update user details API response', e);
      notification.enqueueErrorSnackbar(getErrorMessage(e as AxiosError, 'Could not update licence'));
    }
    setUpdating(false);
  };

  const onRoleUpdate = async (role: UserRoleType): Promise<void> => {
    setUpdating(true);
    try {
      const response = await API.user.updateUserData(details.uuid, { role })();
      const payload: UpdatedUserPayload = response.data;
      postMessage(EventType.UpdatedUser, payload);
      notification.enqueueSuccessSnackbar('User role updated successfully!');
    } catch (e) {
      Logger.error('Invalid update user details API response', e);
      notification.enqueueErrorSnackbar(getErrorMessage(e as AxiosError, 'Could not update user role'));
    }
    setUpdating(false);
  };

  const handleOpenRoleMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setRoleMenuAnchorEl(event.currentTarget);
  };
  const handleCloseRoleMenu = (): void => {
    setRoleMenuAnchorEl(null);
  };
  const onSelectedOption = (role: UserRoleType): void => {
    onRoleUpdate(role);
    handleCloseRoleMenu();
  };

  return (
    <>
      <Grid
        container
        style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.divider}`, minHeight: '68px' }}
        alignItems="center"
      >
        <SectionGrid item container xs={12} alignContent="flex-start">
          <TabTitle>Product Access</TabTitle>
        </SectionGrid>
      </Grid>
      <Grid container rowSpacing="16px" sx={{ padding: '20px' }}>
        {session.user?.scopedPermissions ? (
          <Row name="Units and roles" info={<RowRoleInfo />}>
            <RolesAtUnits />
          </Row>
        ) : (
          <Row name="Role" info={<RoleInfo />}>
            <RowValue>
              <Chip
                disabled={session.user?.uuid === details.uuid || disabled}
                label={UserRoleNames[details.role]}
                variant="outlined"
                size="medium"
                fontSize={14}
                iconRight={
                  roleMenuAnchorEl ? (
                    <ExpandLessIcon sx={{ color: 'text.secondary' }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
                  )
                }
                iconRightDisableInnerMargin
                onClick={handleOpenRoleMenu}
                colors={{
                  background: colors.common.white,
                  border: colors.action.disabled,
                  hoverBackground: colors.divider,
                }}
                enableHover
              />
              <Menu
                keepMounted
                anchorEl={roleMenuAnchorEl}
                onClose={handleCloseRoleMenu}
                open={Boolean(roleMenuAnchorEl)}
              >
                {[UserRoleType.AccountManager, UserRoleType.Manager, UserRoleType.User].map((role) => (
                  <MenuItem key={role} onClick={() => onSelectedOption(role)}>
                    {UserRoleNames[role]}
                  </MenuItem>
                ))}
              </Menu>
            </RowValue>
          </Row>
        )}
        <Row name="Product licences">
          <ProductLicencesGroup>
            <div>
              <p>Lone Working</p>
              <Switch
                disabled={disabled}
                size="small"
                id="productLicenses.alarms"
                checked={details.licenses.protectorAlarms}
                onChange={(e) => onChangeLicense('protectorAlarms', e.target.checked)}
              />
            </div>
            <Divider sx={{ width: '100%' }} />

            {session.hasEventsLicense && (
              <div>
                <p>Events</p>
                <Switch
                  disabled={disabled}
                  size="small"
                  id="productLicenses.events"
                  checked={details.licenses.events}
                  onChange={(e) => onChangeLicense('events', e.target.checked)}
                />
              </div>
            )}

            <Divider sx={{ width: '100%' }} />
            <div>
              <p>Audits</p>
              <Switch
                disabled={disabled}
                size="small"
                id="productLicenses.audits"
                checked={details.licenses.workflows}
                onChange={(e) => onChangeLicense('workflows', e.target.checked)}
              />
            </div>

            <Divider sx={{ width: '100%' }} />
            <div>
              <p>Documents</p>
              <Switch
                disabled={disabled}
                size="small"
                id="productLicenses.documents"
                checked={details.licenses.documents}
                onChange={(e) => onChangeLicense('documents', e.target.checked)}
              />
            </div>

            {session.hasRisksLicense && (
              <>
                <Divider sx={{ width: '100%' }} />
                <div>
                  <p>Risk Register</p>
                  <Switch
                    disabled={disabled}
                    size="small"
                    id="productLicenses.risks"
                    checked={details.licenses.risks}
                    onChange={(e) => onChangeLicense('risks', e.target.checked)}
                  />
                </div>
              </>
            )}
          </ProductLicencesGroup>
        </Row>
      </Grid>
    </>
  );
};

export default observer(ProductAccess);
